import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  WhatsApp,
  Save,
  Add,
  Delete,
} from '@mui/icons-material';
import api from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const SocialLinksConfig = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    whatsapp: '',
    tiktok: '',
  });

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const loadSocialLinks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/social-links');
      if (response.data) {
        // Only extract the social media platform fields, exclude id, created_at, updated_at
        const { facebook, instagram, whatsapp, tiktok } = response.data;
        setSocialLinks({
          facebook: facebook || '',
          instagram: instagram || '',
          whatsapp: whatsapp || '',
          tiktok: tiktok || '',
        });
      }
    } catch (error) {
      console.error('Failed to load social links:', error);
      // Don't show error for initial load, just use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (platform, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.post('/admin/social-links', socialLinks);
      showSuccess('Social links updated successfully!');
    } catch (error) {
      console.error('Failed to save social links:', error);
      showError('Failed to save social links. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty URLs are allowed
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return <Facebook />;
      case 'instagram':
        return <Instagram />;
      case 'whatsapp':
        return <WhatsApp />;
      case 'tiktok':
        return <Typography variant="h6">TikTok</Typography>;
      default:
        return null;
    }
  };

  const getPlatformLabel = (platform) => {
    switch (platform) {
      case 'facebook':
        return 'Facebook';
      case 'instagram':
        return 'Instagram';
      case 'whatsapp':
        return 'WhatsApp';
      case 'tiktok':
        return 'TikTok';
      default:
        return platform;
    }
  };

  const getPlatformPlaceholder = (platform) => {
    switch (platform) {
      case 'facebook':
        return 'https://facebook.com/yourrestaurant';
      case 'instagram':
        return 'https://instagram.com/yourrestaurant';
      case 'whatsapp':
        return 'https://wa.me/1234567890';
      case 'tiktok':
        return 'https://tiktok.com/@yourrestaurant';
      default:
        return 'https://example.com';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Social Media Links Configuration
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure your restaurant's social media links. These will be displayed on your public website footer.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Social Media Platforms
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Enter the full URLs for each social media platform. Leave empty to hide the platform from your website.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {Object.entries(socialLinks).map(([platform, url]) => (
            <Grid item xs={12} md={6} key={platform}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getPlatformIcon(platform)}
                    <Typography variant="h6" fontWeight="bold">
                      {getPlatformLabel(platform)}
                    </Typography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    label={`${getPlatformLabel(platform)} URL`}
                    value={url}
                    onChange={(e) => handleInputChange(platform, e.target.value)}
                    placeholder={getPlatformPlaceholder(platform)}
                    error={!validateUrl(url)}
                    helperText={
                      !validateUrl(url) 
                        ? 'Please enter a valid URL (e.g., https://example.com)'
                        : 'Enter the full URL including https://'
                    }
                    variant="outlined"
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={loadSocialLinks}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !Object.values(socialLinks).some(url => validateUrl(url))}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Social media links will appear in the footer of your public website. 
          Make sure to use the correct URLs for each platform to ensure proper functionality.
        </Typography>
      </Alert>
    </Box>
  );
};

export default SocialLinksConfig;
